import React, { memo } from 'react';
import useAuth from '../../hooks/useAuth';

/**
 * Componente de protección de rutas que requiere autenticación
 * @param {Object} props - Propiedades del componente
 * @param {React.ReactNode} props.children - Componentes hijos a renderizar si está autenticado
 * @param {React.ReactNode} props.fallback - Componente a mostrar mientras se verifica la autenticación
 */
const ProtectedRoute = memo(({ children, fallback = null }) => {
  const { isAuthenticated, loading, error, user } = useAuth();

  // DEBUG: Logs detallados para debugging (solo una vez por render)
  console.group('🔍 ProtectedRoute Debug - Render');
  console.log('isAuthenticated:', isAuthenticated);
  console.log('loading:', loading);
  console.log('error:', error);
  console.log('user:', user);
  console.log('localStorage sanctum_token:', localStorage.getItem('sanctum_token') ? 'Presente' : 'Ausente');
  console.log('localStorage user_data:', localStorage.getItem('user_data') ? 'Presente' : 'Ausente');
  console.groupEnd();

  // Mostrar componente de carga mientras se verifica la autenticación
  if (loading) {
    return fallback || (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div>Verificando autenticación...</div>
        </div>
      </div>
    );
  }

  // Mostrar error si hay problemas de autenticación
  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#dc3545',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ marginBottom: '20px', fontSize: '48px' }}>⚠️</div>
          <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>Error de autenticación</div>
          <div>{error}</div>
          <div style={{ marginTop: '20px', fontSize: '14px', color: '#6c757d' }}>
            Por favor, intenta acceder nuevamente desde el sistema principal.
          </div>
        </div>
      </div>
    );
  }

  // Si no está autenticado, mostrar información de debug
  if (!isAuthenticated) {
    console.warn('❌ Usuario no autenticado en ProtectedRoute');
    
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div>Redirigiendo al sistema de autenticación...</div>
          
          {/* Información de debug en desarrollo */}
          {'development' === 'development' && (
            <div style={{ 
              marginTop: '20px', 
              padding: '15px', 
              backgroundColor: '#f8f9fa', 
              border: '1px solid #dee2e6',
              borderRadius: '5px',
              fontSize: '12px',
              textAlign: 'left',
              maxWidth: '400px'
            }}>
              <strong>Debug Info:</strong><br/>
              Token: {localStorage.getItem('sanctum_token') ? '✅ Presente' : '❌ Ausente'}<br/>
              User Data: {localStorage.getItem('user_data') ? '✅ Presente' : '❌ Ausente'}<br/>
              Loading: {loading ? 'Sí' : 'No'}<br/>
              Error: {error || 'Ninguno'}<br/>
              isAuthenticated: {isAuthenticated ? 'Sí' : 'No'}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Si está autenticado, renderizar los componentes hijos
  return children;
});

// Agregar nombre para debugging
ProtectedRoute.displayName = 'ProtectedRoute';

export default ProtectedRoute;